import { Box, Stack } from '@mui/material'
import { CarrierVisitStatus } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { InitializationWrapper } from '@planning/components'
import { useRailVisitBreadcrumbs } from '@planning/hooks/useRailVisitBreadcrumbs'
import { GetRailcarsQuery } from '@planning/messages/queries'
import { railVisitService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, ConfirmationDialog } from '@tom-ui/ui'
import { PATHS, withAuthenticationRequired } from '@tom-ui/utils'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { RailVisitForm } from './Components/Forms/RailVisitForm'
import { RailVisitPlanningPageHeader } from './Components/Molecules/RailVisitPlanningPageHeader'
import { RailVisitPlanningWarnings } from './Components/Molecules/RailVisitPlanningWarnings'
import { RailVisitPlanning } from './Components/Planning/RailVisitPlanning'
import { RailVisitFormStore, RailVisitFormStoreInitialData } from './stores/RaillVisitFormStore'
import { RailVisitVoyageDto } from './stores/RailVisitGeneralInformationFormStore'
import { RailVisitPlanningViewStore } from './stores/RailVisitPlanningViewStore'

export const RailVisitPlanningPage: FC = withAuthenticationRequired(
  observer(() => {
    const { id } = useParams()
    const {
      railVisitPlanningStore,
      railVisitItemStore,
      railTrackItemStore,
      railcarTrackPositionItemStore,
      railcarItemStore,
    } = usePlanningStore()

    const [loading, setLoading] = useState(true)

    const railVisitPlanningViewStore = useMemo(
      () =>
        new RailVisitPlanningViewStore(
          railTrackItemStore,
          railVisitItemStore,
          railcarTrackPositionItemStore,
        ),
      [railTrackItemStore, railVisitItemStore, railcarTrackPositionItemStore],
    )

    useEffect(() => {
      const requests = [
        railTrackItemStore.fetch(),
        railcarItemStore.fetch(new GetRailcarsQuery(1, 1000)),
      ]

      if (id) {
        railVisitPlanningStore.setIsEditMode(true)
        const visitId = Number(id)

        Promise.all(requests).then(async () => {
          const railVisit = await railVisitService.getById(visitId)
          railVisitItemStore.upsertBulk([railVisit])
          const railVisitItem = railVisitItemStore.getById(visitId)
          railVisitPlanningStore.setRailVisit(railVisitItem)

          if (railVisit) {
            railVisitPlanningViewStore.setVisitSelectedId(visitId)
            railVisitPlanningViewStore.setStartDateIfNewDateHasADifferentStart(railVisit.eta)
          }

          setLoading(false)
        })
      } else {
        railVisitPlanningStore.setIsEditMode(false)
        railVisitPlanningStore.reset()
        setLoading(false)
        Promise.all(requests)
      }
    }, [
      id,
      railTrackItemStore,
      railVisitItemStore,
      railVisitPlanningStore,
      railVisitPlanningViewStore,
      railcarItemStore,
    ])

    return (
      <InitializationWrapper isInitializing={loading}>
        <RailVisitPlanningControl railVisitPlanningViewStore={railVisitPlanningViewStore} />
      </InitializationWrapper>
    )
  }),
)

type Props = { railVisitPlanningViewStore: RailVisitPlanningViewStore }

const RailVisitPlanningControl: FC<Props> = observer(props => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const { appViewStore, railVisitPlanningStore } = usePlanningStore()

  const { railVisitPlanningViewStore } = props

  const onFormSubmit = async () => {
    if (formStore.validate()) {
      if (railVisitPlanningStore.railVisit?.id) {
        if (formStore.isDirty) {
          await railVisitPlanningStore.updateRailVisit(
            railVisitPlanningStore.railVisit.id,
            formStore.data,
          )
        }
      } else {
        await railVisitPlanningStore.createRailVisit(formStore.data)

        railVisitPlanningStore.reset()
      }

      goBackToOrigin(location.state?.from, railVisitPlanningStore.railVisit?.id)

      appViewStore.setShowAlert(
        'success',
        t('railVisitSubmitSuccessfully', 'Rail visit submit successfully'),
      )
    }
  }

  const formStore = useMemo(() => {
    const rv = railVisitPlanningStore.railVisit

    const initialData: RailVisitFormStoreInitialData = {
      railVisit: rv?.data,
      voyages: rv?.data.inboundTripIds?.map<RailVisitVoyageDto>((x, index) => ({
        inboundNumber: x,
        outboundNumber: rv?.data.outboundTripIds![index] ?? '',
      })),
      railTracks: rv?.railTracks,
    }

    return new RailVisitFormStore(initialData)
  }, [railVisitPlanningStore.railVisit])

  const { breadcrumbs } = useRailVisitBreadcrumbs(railVisitPlanningStore.railVisit)

  const location = useLocation()

  const goBackToOrigin = (cameFrom: string, id?: number) => {
    const basePath = PATHS.visits.railVisits.url

    if (cameFrom === 'dashboard') {
      navigate(`${basePath}/${id}/dashboard`)
    } else if (cameFrom === 'rail-visits') {
      navigate(PATHS.visits.railVisits.url)
    } else {
      navigate(basePath)
    }
  }

  useEffect(() => {
    const disposer = reaction(
      () => formStore.data,
      formValues => {
        railVisitPlanningViewStore.applyFormStoreDataToPlanning(
          railVisitPlanningStore.railVisit?.id || 0,
          formValues,
        )
      },
    )

    return () => {
      disposer()
    }
  }, [railVisitPlanningViewStore, formStore.data, railVisitPlanningStore.railVisit?.id])

  const title = railVisitPlanningStore.isEditMode
    ? t('editRailVisit', 'Edit rail visit')
    : t('createRailVisit', 'Create rail visit')

  const isVisitClosed = railVisitPlanningStore.railVisit?.data.status === CarrierVisitStatus.Closed

  return (
    <Box sx={{ height: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <RailVisitPlanningPageHeader
        pageTitle={title}
        submitButtonLabel={
          railVisitPlanningStore.isEditMode ? t('saveAll', 'Save all') : t('submit', 'Submit')
        }
        hideSubmitButton={isVisitClosed}
        onBack={() => {
          if (formStore.isDirty && !isVisitClosed) {
            railVisitPlanningViewStore.toggleConfirmationDialog(true)
          } else {
            goBackToOrigin(location.state?.from, railVisitPlanningStore.railVisit?.id)
            railVisitPlanningStore.reset()
          }
        }}
        onSubmit={onFormSubmit}
      />

      <Stack direction='row' spacing={1} sx={{ height: 'calc(100% - 162px)' }}>
        <Stack
          sx={{
            padding: '0 1rem 1rem 1rem',
            height: '100%',
            overflowY: 'auto',
            flex: 2,
            gap: 2,
          }}
        >
          <RailVisitPlanningWarnings railVisitPlanningViewStore={railVisitPlanningViewStore} />
          <RailVisitForm
            formStore={formStore}
            allowEdit={!isVisitClosed}
            hideActions={isVisitClosed}
          />
        </Stack>
        <Stack
          sx={{
            flex: 5,
            height: '100%',
            overflowY: 'auto',
            padding: 1,
          }}
        >
          <RailVisitPlanning store={railVisitPlanningViewStore} />
        </Stack>
      </Stack>

      <ConfirmationDialog
        open={railVisitPlanningViewStore.isDialogOpen}
        title={t('discardChanges', 'Discard changes?')}
        message={
          railVisitPlanningStore.isEditMode
            ? t('unsavedChangesWarning', 'You will lose unsaved changes.')
            : t('allDataWillBeLost', 'All entered data will be lost.')
        }
        primaryActionText={t('discard', 'Discard')}
        closeLabel={t('keepEditing', 'Keep editing')}
        maxWidth='xs'
        blockClickOutside
        onConfirm={() => {
          goBackToOrigin(location.state?.from, railVisitPlanningStore.railVisit?.id)

          railVisitPlanningStore.reset()
        }}
        onClose={() => railVisitPlanningViewStore.toggleConfirmationDialog(false)}
      />
    </Box>
  )
})
